import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Box, Card, CardContent, Grid, Stack, Typography, colors, useTheme } from "@mui/material";
import { Pie } from "react-chartjs-2";

import { useAppDispatch } from "../../app/hooks";
import { changePageTitle } from "../preferences/preferencesSlice";
import { EmployeeStatus } from "../process/types/processTypes";
import { useTranslation } from "react-i18next";
import type { Chart, LegendItem } from "chart.js";
import { ChartOptions } from "chart.js";
import { TotalValues } from "./TotalValues";
import { apolloClient } from "../../apollo/graphql";
import { GET_EMPLOYEE_DASHBOARD_STATS } from "../../apollo/queries/processes";
import { FollowUpDashboardCards } from "../follow-ups/components/FollowUpDashboardCards";
import { FollowUpTypeCountRow } from "../process/types/processTypes";

const DASHBOARD_STATUS_ORDER: EmployeeStatus[] = [
  EmployeeStatus.new,
  EmployeeStatus.selected,
  EmployeeStatus.contracted,
  EmployeeStatus.visa_issued,
  EmployeeStatus.labour_issued,
  EmployeeStatus.ticketing,
  EmployeeStatus.flight_booked,
  EmployeeStatus.departed,
  EmployeeStatus.lost,
  EmployeeStatus.returned,
  EmployeeStatus.contract_completed,
  EmployeeStatus.other,
];

const DASHBOARD_STATUS_SET = new Set<string>(DASHBOARD_STATUS_ORDER);

function normalizeEmployeeStatusForDashboard(
  status: string | undefined | null
): EmployeeStatus {
  if (status && DASHBOARD_STATUS_SET.has(status)) {
    return status as EmployeeStatus;
  }
  return EmployeeStatus.other;
}

function toTitleCaseLabel(value: string): string {
  return value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function createPieLegendGenerateLabels(denominator: number) {
  return function generateLabels(chart: Chart): LegendItem[] {
    const ds = chart.data.datasets[0];
    const labels = chart.data.labels ?? [];
    const nums = (ds.data as number[]).map((n) => Number(n));
    const denom =
      denominator > 0 ? denominator : nums.reduce((sum, n) => sum + n, 0);
    const bg = ds.backgroundColor;
    const backgrounds = Array.isArray(bg)
      ? bg
      : labels.map(() => String(bg ?? "#ccc"));
    const items: LegendItem[] = [];

    for (let i = 0; i < labels.length; i++) {
      if (nums[i] <= 0) continue;
      const pct =
        denom > 0 ? ((nums[i] * 100) / denom).toFixed(1) : "0.0";
      const visible = chart.getDataVisibility(i);
      items.push({
        text: `${labels[i]} (${pct}%)`,
        fillStyle: backgrounds[i] as string,
        strokeStyle:
          typeof ds.borderColor === "string" ? ds.borderColor : "#fff",
        lineWidth: typeof ds.borderWidth === "number" ? ds.borderWidth : 2,
        hidden: visible === false,
        index: i,
        datasetIndex: 0,
      });
    }
    return items;
  };
}

const Dashboard = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const labels = useMemo(
    () => [
      te("new"),
      te("selected"),
      te("contracted"),
      te("visa_issued"),
      te("labour_issued"),
      te("flight_booked"),
      te("departed"),
      te("lost"),
      te("returned"),
      te("contract_completed"),
      te("other"),
    ],
    [te]
  );
  const [values, setValues] = useState<number[]>(() =>
    Array(DASHBOARD_STATUS_ORDER.length).fill(0)
  );
  const [countryLabels, setCountryLabels] = useState<string[]>([]);
  const [countryValues, setCountryValues] = useState<number[]>([]);
  const [loading, setLoading] = useState<"idle" | "pending">("pending");
  const [totalCount, setTotalCount] = useState(0);
  const [followUpCounts, setFollowUpCounts] = useState<FollowUpTypeCountRow[]>(
    [],
  );

  useEffect(() => {
    dispatch(changePageTitle("Dashboard"));
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;

    async function loadDashboardStats() {
      setLoading("pending");

      try {
        const { data } = await apolloClient.query({
          query: GET_EMPLOYEE_DASHBOARD_STATS,
          fetchPolicy: "network-only",
        });

        if (cancelled) return;

        const stats = data?.employeeDashboardStats;
        if (!stats) {
          if (!cancelled) {
            setTotalCount(0);
            setValues(Array(DASHBOARD_STATUS_ORDER.length).fill(0));
            setCountryLabels([]);
            setCountryValues([]);
          }
          return;
        }

        const groupByCategory: Record<string, number> = {};
        for (const row of stats.byStatus ?? []) {
          const bucket = normalizeEmployeeStatusForDashboard(row.status);
          groupByCategory[bucket] =
            (groupByCategory[bucket] ?? 0) + row.count;
        }

        if (!cancelled) {
          setTotalCount(stats.totalCount);
          setValues(
            DASHBOARD_STATUS_ORDER.map(
              (statusKey) => groupByCategory[statusKey] ?? 0
            )
          );
          const appliedCountries = (stats.byAppliedCountry ?? []).filter(
            (row: { country: string; count: number }) => row.count > 0
          );
          setCountryLabels(
            appliedCountries.map((row: { country: string }) =>
              row.country === "unknown"
                ? "Unknown"
                : toTitleCaseLabel(row.country)
            )
          );
          setCountryValues(
            appliedCountries.map((row: { count: number }) => row.count)
          );
          setFollowUpCounts(stats.followUpCounts ?? []);
        }
      } finally {
        if (!cancelled) {
          setLoading("idle");
        }
      }
    }

    loadDashboardStats();
    return () => {
      cancelled = true;
    };
  }, []);

  const bgColors = [
    colors.orange[500],
    colors.grey[500],
    colors.green[500],
    colors.blue[500],
    colors.deepPurple[500],
    colors.indigo[500],
    colors.grey[500],
    colors.green[500],
    colors.blue[500],
    colors.deepPurple[500],
    colors.indigo[500],
  ];
  const bgColorsBolder = [
    colors.orange[700],
    colors.grey[700],
    colors.green[700],
    colors.blue[700],
    colors.deepPurple[700],
    colors.indigo[700],
    colors.grey[700],
    colors.green[700],
    colors.blue[700],
    colors.deepPurple[700],
    colors.indigo[700],
  ];
  const data = {
    datasets: [
      {
        data: values,
        backgroundColor: bgColors,
        borderWidth: 2,
        borderColor: "white",
        hoverBorderColor: colors.common.white,
      },
    ],
    labels: labels,
  };
  const countryColors = [
    colors.teal[500],
    colors.cyan[500],
    colors.lightBlue[500],
    colors.blue[500],
    colors.indigo[500],
    colors.deepPurple[500],
    colors.purple[500],
    colors.pink[500],
    colors.red[500],
    colors.orange[500],
    colors.amber[500],
    colors.green[500],
    colors.lime[500],
    colors.brown[500],
    colors.blueGrey[500],
  ];
  const countriesData = {
    datasets: [
      {
        data: countryValues,
        backgroundColor: countryLabels.map(
          (_, index) => countryColors[index % countryColors.length]
        ),
        borderWidth: 2,
        borderColor: "white",
        hoverBorderColor: colors.common.white,
      },
    ],
    labels: countryLabels,
  };

  const statusDenominator =
    totalCount > 0 ? totalCount : values.reduce((a, b) => a + b, 0);
  const countryDenominator = countryValues.reduce((a, b) => a + b, 0);

  const pieLegendLabelsBase = {
    usePointStyle: true,
    pointStyle: "circle" as const,
    padding: 14,
    boxWidth: 10,
    boxHeight: 10,
    font: {
      size: 12,
      family: "Open Sans",
      style: "normal" as const,
      weight: "600" as const,
    },
    color: theme.palette.text.secondary,
  };

  const statusPieOptions: ChartOptions<"pie"> = {
    maintainAspectRatio: false,
    responsive: true,
    layout: {
      padding: 8,
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "center",
        title: {
          display: false,
          text: String(t("dashboard")),
          position: "start",
          font: {
            size: 14,
            family: "Open Sans",
            style: "normal",
            weight: "bold",
          },
          color: theme.palette.text.primary,
          padding: {
            top: 2,
            bottom: 6,
          },
        },
        labels: {
          ...pieLegendLabelsBase,
          generateLabels: createPieLegendGenerateLabels(statusDenominator),
        },
      },
      tooltip: {
        backgroundColor: "rgba(25, 30, 48, 0.95)",
        titleFont: { size: 13, weight: "bold" },
        bodyFont: { size: 12 },
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (context) => {
            const value = Number(context.parsed || 0);
            const percentage =
              statusDenominator > 0
                ? ((value * 100) / statusDenominator).toFixed(2)
                : "0.00";
            return `${context.label}: ${value.toLocaleString()} (${percentage}%)`;
          },
        },
      },
    },
  };

  const countryPieOptions: ChartOptions<"pie"> = {
    ...statusPieOptions,
    plugins: {
      ...statusPieOptions.plugins,
      legend: {
        ...statusPieOptions.plugins?.legend,
        labels: {
          ...pieLegendLabelsBase,
          generateLabels: createPieLegendGenerateLabels(countryDenominator),
        },
      },
      tooltip: {
        ...statusPieOptions.plugins?.tooltip,
        callbacks: {
          label: (context) => {
            const value = Number(context.parsed || 0);
            const percentage =
              countryDenominator > 0
                ? ((value * 100) / countryDenominator).toFixed(2)
                : "0.00";
            return `${context.label}: ${value.toLocaleString()} (${percentage}%)`;
          },
        },
      },
    },
  };

  const ready =
    loading === "idle" &&
    labels.length === DASHBOARD_STATUS_ORDER.length &&
    values.length === DASHBOARD_STATUS_ORDER.length;
  const hasCountryData = countryValues.length > 0;

  return (
    <>
      <Helmet>
        <title> {`${t("dashboard")} | ${t("platform")}`}</title>
      </Helmet>
      <Box
        sx={{
          m: 1.5,
        }}
      >
        {ready && (
          <Grid container spacing={2}>
            <Grid container item md={3} xs={12} spacing={2}>
              <Grid item xs={12}>
                <TotalValues
                  key={1}
                  label={labels[1]}
                  value={values[1]}
                  totalCount={totalCount}
                  loading={loading}
                  color={bgColors[1]}
                  avatarColor={bgColorsBolder[1]}
                />
              </Grid>
              <Grid item xs={12}>
                <TotalValues
                  key={2}
                  label={labels[2]}
                  value={values[2]}
                  totalCount={totalCount}
                  loading={loading}
                  color={bgColors[2]}
                  avatarColor={bgColorsBolder[2]}
                />
              </Grid>
              <Grid item xs={12}>
                <TotalValues
                  key={3}
                  label={labels[3]}
                  value={values[3]}
                  totalCount={totalCount}
                  loading={loading}
                  color={bgColors[3]}
                  avatarColor={bgColorsBolder[3]}
                />
              </Grid>
              <Grid item xs={12}>
                <TotalValues
                  key={4}
                  label={labels[4]}
                  value={values[4]}
                  totalCount={totalCount}
                  loading={loading}
                  color={bgColors[4]}
                  avatarColor={bgColorsBolder[4]}
                />
              </Grid>
              <Grid item xs={12}>
                <TotalValues
                  key={5}
                  label={labels[5]}
                  value={values[5]}
                  totalCount={totalCount}
                  loading={loading}
                  color={bgColors[5]}
                  avatarColor={bgColorsBolder[5]}
                />
              </Grid>
            </Grid>
            <Grid item md={6} xs={12}>
              <Stack spacing={2}>
                <TotalValues
                  key={0}
                  label={labels[0]}
                  value={values[0]}
                  totalCount={totalCount}
                  loading={loading}
                  color={bgColors[0]}
                  avatarColor={bgColorsBolder[0]}
                />
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "0 8px 24px rgba(18, 38, 63, 0.08)",
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary", mb: 2 }}>
                      Current Status
                    </Typography>
                    <Box sx={{ height: { xs: 300, md: 420 } }}>
                      <Pie options={statusPieOptions} data={data} />
                    </Box>
                  </CardContent>
                </Card>
                {hasCountryData && (
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: "0 8px 24px rgba(18, 38, 63, 0.08)",
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary", mb: 2 }}>
                        Applied Countries
                      </Typography>
                      <Box sx={{ height: { xs: 300, md: 380 } }}>
                        <Pie options={countryPieOptions} data={countriesData} />
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </Grid>
            <Grid container item md={3} xs={12} spacing={2}>
              <Grid item xs={12}>
                <TotalValues
                  key={6}
                  label={labels[6]}
                  value={values[6]}
                  totalCount={totalCount}
                  loading={loading}
                  color={bgColors[6]}
                  avatarColor={bgColorsBolder[6]}
                />
              </Grid>
              <Grid item xs={12}>
                <TotalValues
                  key={7}
                  label={labels[7]}
                  value={values[7]}
                  totalCount={totalCount}
                  loading={loading}
                  color={bgColors[7]}
                  avatarColor={bgColorsBolder[7]}
                />
              </Grid>
              <Grid item xs={12}>
                <TotalValues
                  key={8}
                  label={labels[8]}
                  value={values[8]}
                  totalCount={totalCount}
                  loading={loading}
                  color={bgColors[8]}
                  avatarColor={bgColorsBolder[8]}
                />
              </Grid>
              <Grid item xs={12}>
                <TotalValues
                  key={9}
                  label={labels[9]}
                  value={values[9]}
                  totalCount={totalCount}
                  loading={loading}
                  color={bgColors[9]}
                  avatarColor={bgColorsBolder[9]}
                />
              </Grid>
              <Grid item xs={12}>
                <TotalValues
                  key={10}
                  label={labels[10]}
                  value={values[10]}
                  totalCount={totalCount}
                  loading={loading}
                  color={bgColors[10]}
                  avatarColor={bgColorsBolder[10]}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        <FollowUpDashboardCards
          counts={followUpCounts}
          loading={loading === "pending"}
        />
      </Box>
    </>
  );
};

export default Dashboard;
